import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { fieldWorkApi } from '../api/field-work-api';
import { ConflictError } from '../api/errors';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect } from '../components/SearchableSelect';
import { useAuth } from '../auth/AuthContext';
import { fieldOpsPeopleApi } from '../api/field-ops-people-api';
import { inventoryApi } from '../api/inventory-api-instance';
import {
  canHandOff,
  currentHandoff,
  fieldWorkPriorities,
  isOpenStatus,
  logisticsModeLabels,
  priorityLabels,
  sourceLabels,
  statusLabels,
  statusTransitions,
  type FieldWorkPriority,
  type FieldWorkRequest,
  type FieldWorkRequestPatch,
} from '../domain/fieldWorkRequest';
import { fieldOpsTeams, teamLabels, type FieldOpsTeam } from '../domain/fieldOpsTeam';
import { ActivityLog } from '../features/field-work/ActivityLog';
import { HandOffDialog } from '../features/field-work/HandOffDialog';
import { PickListsSection } from '../features/field-work/PickListsSection';
import { NewShipmentDrawer } from '../features/shipment-create/NewShipmentDrawer';

function formatDuration(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'none';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours >= 24) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function FieldWorkRequestDetailPage() {
  const { requestId = '' } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [blockerDraft, setBlockerDraft] = useState('');
  const [completionDraft, setCompletionDraft] = useState('');
  const [githubDraft, setGithubDraft] = useState('');
  const [showCargonizer, setShowCargonizer] = useState(false);
  const [handingOff, setHandingOff] = useState(false);

  const request = useQuery({
    queryKey: ['field-work', requestId],
    queryFn: () => fieldWorkApi.getFieldWork(requestId),
  });
  const people = useQuery({
    queryKey: ['field-ops-people'],
    queryFn: () => fieldOpsPeopleApi.listFieldOpsPeople(),
    staleTime: 30 * 60 * 1000,
  });

  const data = request.data;

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(data?.customerId),
  });
  const sites = useQuery({
    queryKey: ['sites', data?.customerId ?? ''],
    queryFn: () => inventoryApi.listSites(data?.customerId),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(data?.customerId && data?.siteId),
  });
  const customerName =
    (customers.data ?? []).find((customer) => customer.id === data?.customerId)?.name ?? null;
  const site = (sites.data ?? []).find((candidate) => candidate.id === data?.siteId) ?? null;

  const assigneeOptions = useMemo(() => {
    const directory = (people.data ?? [])
      .map((person) => ({
        value: person.email,
        label: person.displayName ? `${person.displayName} · ${person.email}` : person.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const options = [{ value: '', label: 'Unassigned' }, ...directory];
    if (data?.assignee && !directory.some((option) => option.value === data.assignee)) {
      options.push({ value: data.assignee, label: `${data.assignee} (not in directory)` });
    }
    return options;
  }, [people.data, data?.assignee]);

  // Seed the editable text drafts once per record. Keying on data itself
  // would re-seed on every mutation success and every background refetch,
  // wiping whatever the user is mid-way through typing.
  useEffect(() => {
    if (!data) return;
    setBlockerDraft(data.blocker ?? '');
    setCompletionDraft(data.completionNotes ?? '');
    setGithubDraft(data.githubIssueUrl ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const mutation = useMutation({
    mutationFn: (patch: Omit<FieldWorkRequestPatch, 'id' | 'version'>) => {
      if (!data) throw new Error('Request not loaded');
      return fieldWorkApi.updateFieldWork({ id: data.id, version: data.version, ...patch });
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(['field-work', requestId], updated);
      await queryClient.invalidateQueries({ queryKey: ['field-work'] });
      setFormError(null);
      setConflict(false);
    },
    onError: async (error: unknown) => {
      if (error instanceof ConflictError) {
        setConflict(true);
        setFormError(null);
        await request.refetch();
        return;
      }
      setFormError(error instanceof Error ? error.message : 'The change could not be saved.');
    },
  });

  function showUpdate(updated: FieldWorkRequest): void {
    queryClient.setQueryData(['field-work', requestId], updated);
    void queryClient.invalidateQueries({ queryKey: ['field-work'] });
  }

  if (request.isLoading) return <PageLoadingState label="Loading request…" />;
  if (request.isError || !data) {
    return (
      <ErrorState
        title="Field work request could not be loaded"
        rpcName="GetFieldWorkRequest"
        onRetry={() => void request.refetch()}
      />
    );
  }

  const disabled = mutation.isPending;
  const handoff = currentHandoff(data);

  return (
    <>
      <PageHeader
        backLink={<Link to="/field-work">Field work</Link>}
        title={data.title || 'Untitled request'}
        description={`${teamLabels[data.team]} team · ${statusLabels[data.status]}`}
      />

      {handoff ? (
        <div className="info-callout handoff-callout" role="status">
          <strong>
            Handed to {handoff.toAssignee === user?.email ? 'you' : handoff.toAssignee} by{' '}
            {handoff.author ?? 'someone'}
            {handoff.createTime ? ` · ${new Date(handoff.createTime).toLocaleString()}` : ''}
          </strong>
          <p>{handoff.note}</p>
        </div>
      ) : null}
      {conflict ? (
        <p role="alert" className="form-error">
          This request changed since you loaded it. The latest version is shown — review it and
          reapply your change.
        </p>
      ) : null}
      {formError ? (
        <p role="alert" className="form-error">
          {formError}
        </p>
      ) : null}

      <div className="request-layout">
        <aside className="request-notes" aria-label="Notes">
          <ActivityLog request={data} onChanged={showUpdate} />
        </aside>
        <div className="request-main">
          <dl className="site-details">
            <dt>Description</dt>
            <dd>{data.description || '—'}</dd>
            <dt>Source</dt>
            <dd>
              {data.source ? (
                <Link to={`/field-work?source=${data.source}`}>{sourceLabels[data.source]}</Link>
              ) : (
                '—'
              )}
            </dd>
            <dt>Customer / site</dt>
            <dd>
              {data.customerId ? (
                <Link to={`/customers/${data.customerId}`}>{customerName ?? 'Customer'}</Link>
              ) : (
                '—'
              )}
              {data.siteId ? ` · ${site?.name ?? `site ${data.siteId}`}` : ''}
            </dd>
            <dt>Related assets</dt>
            <dd>
              {data.relatedItemIds.length
                ? data.relatedItemIds.map((id, index) => (
                    <span key={id}>
                      {index > 0 ? ', ' : ''}
                      <Link to={`/inventory/${encodeURIComponent(id)}`}>{id}</Link>
                    </span>
                  ))
                : '—'}
            </dd>
            <dt>Logistics</dt>
            <dd>
              {data.logistics ? logisticsModeLabels[data.logistics.mode] : 'No shipping required'}
              {data.logistics?.notes ? ` — ${data.logistics.notes}` : ''}
            </dd>
            <dt>Queue position</dt>
            <dd>{data.queuePosition > 0 ? data.queuePosition : 'Unranked'}</dd>
            <dt>Due date</dt>
            <dd>{data.dueDate ? new Date(data.dueDate).toLocaleDateString() : '—'}</dd>
            <dt>Requested</dt>
            <dd>
              {new Date(data.requestedAt).toLocaleString()}
              {data.requestedBy ? (
                <>
                  {' by '}
                  <Link to={`/field-work?requester=${encodeURIComponent(data.requestedBy)}`}>
                    {data.requestedBy}
                  </Link>
                </>
              ) : null}
            </dd>
            {data.triagedAt ? (
              <>
                <dt>Triaged</dt>
                <dd>{new Date(data.triagedAt).toLocaleString()}</dd>
              </>
            ) : null}
            {data.startedAt ? (
              <>
                <dt>Started</dt>
                <dd>{new Date(data.startedAt).toLocaleString()}</dd>
              </>
            ) : null}
            {data.completedAt ? (
              <>
                <dt>Completed</dt>
                <dd>{new Date(data.completedAt).toLocaleString()}</dd>
              </>
            ) : null}
            <dt>Time blocked</dt>
            <dd>{formatDuration(data.blockedTotalSeconds)}</dd>
          </dl>

          <section className="drawer-section" aria-label="Queue owner actions">
            <h2>Prioritize &amp; assign</h2>
            <div className="form-row">
              <label>
                Priority
                <SearchableSelect
                  ariaLabel="Priority"
                  value={data.priority ?? ''}
                  onChange={(value) =>
                    mutation.mutate({
                      priority: (value || undefined) as FieldWorkPriority | undefined,
                    })
                  }
                  options={[
                    { value: '', label: 'Unset' },
                    ...fieldWorkPriorities.map((priority) => ({
                      value: priority,
                      label: priorityLabels[priority],
                    })),
                  ]}
                  placeholder="Unset"
                />
              </label>
              <label>
                Field Ops team
                <SearchableSelect
                  ariaLabel="Field Ops team"
                  value={data.team}
                  onChange={(value) => mutation.mutate({ team: value as FieldOpsTeam })}
                  options={fieldOpsTeams.map((team) => ({ value: team, label: teamLabels[team] }))}
                  placeholder="Team"
                />
              </label>
              <label>
                Assignee
                <SearchableSelect
                  ariaLabel="Assignee"
                  value={data.assignee}
                  onChange={(value) => mutation.mutate({ assignee: value })}
                  options={assigneeOptions}
                  placeholder="Unassigned"
                />
              </label>
            </div>
            <div className="drawer-actions">
              {canHandOff(data) ? (
                <button type="button" disabled={disabled} onClick={() => setHandingOff(true)}>
                  Hand off…
                </button>
              ) : null}
              {user?.email && data.assignee !== user.email ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => mutation.mutate({ assignee: user.email })}
                >
                  Assign to me
                </button>
              ) : null}
            </div>
          </section>

          <section className="drawer-section" aria-label="Status">
            <h2>Status: {statusLabels[data.status]}</h2>
            <p className="drawer-version-note">
              {isOpenStatus(data.status)
                ? 'Move it to one of the states below.'
                : 'This request is closed. Use “Reopen…” below to put it back in play — that clears the completion timestamp.'}
            </p>
            <label>
              Blocker
              <input
                value={blockerDraft}
                onChange={(event) => setBlockerDraft(event.target.value)}
                placeholder="What is this waiting on? (required to move to Blocked)"
              />
            </label>
            <label>
              Completion notes / result
              <textarea
                value={completionDraft}
                onChange={(event) => setCompletionDraft(event.target.value)}
                placeholder="Result / outcome (required to Complete)"
                rows={2}
              />
            </label>
            <div className="drawer-actions">
              {statusTransitions[data.status].map((status) => {
                const reopen = !isOpenStatus(data.status) && isOpenStatus(status);
                return (
                  <button
                    key={status}
                    type="button"
                    disabled={disabled}
                    className={status === 'COMPLETE' ? 'primary' : undefined}
                    onClick={() =>
                      mutation.mutate({
                        status,
                        ...(status === 'BLOCKED' ? { blocker: blockerDraft.trim() } : {}),
                        ...(status === 'COMPLETE'
                          ? { completionNotes: completionDraft.trim() }
                          : {}),
                      })
                    }
                  >
                    {reopen ? `Reopen as ${statusLabels[status]}` : statusLabels[status]}
                  </button>
                );
              })}
            </div>
            <p className="drawer-version-note">
              Forward through the lifecycle only — New → Triaged → In progress → Complete, with
              Blocked and Rejected off to the side.
            </p>
          </section>

          <section className="drawer-section" aria-label="Links">
            <h2>Links</h2>
            <label>
              GitHub issue
              <input
                type="url"
                value={githubDraft}
                onChange={(event) => setGithubDraft(event.target.value)}
                placeholder="https://github.com/tidaltech/koi/issues/…"
              />
            </label>
            <div className="drawer-actions">
              <button
                type="button"
                disabled={disabled || githubDraft.trim() === (data.githubIssueUrl ?? '')}
                onClick={() => mutation.mutate({ githubIssueUrl: githubDraft.trim() })}
              >
                Save GitHub link
              </button>
              {data.githubIssueUrl ? (
                <a href={data.githubIssueUrl} target="_blank" rel="noreferrer">
                  Open issue
                </a>
              ) : null}
            </div>
          </section>

          <section className="drawer-section" aria-label="Logistics">
            <h2>Logistics</h2>
            <p>
              {data.logistics && data.logistics.mode !== 'NONE'
                ? logisticsModeLabels[data.logistics.mode]
                : 'No shipping recorded'}
              {data.logistics?.notes ? ` — ${data.logistics.notes}` : ''}
            </p>
            {data.logistics?.shipmentId ? (
              <p>
                Linked shipment{' '}
                <Link to={`/shipments/${data.logistics.shipmentId}`}>
                  {data.logistics.shipmentId}
                </Link>
              </p>
            ) : null}
            <div className="drawer-actions">
              <button type="button" disabled={disabled} onClick={() => setShowCargonizer(true)}>
                {data.logistics?.shipmentId
                  ? 'Book another Cargonizer shipment'
                  : 'Book with Cargonizer'}
              </button>
            </div>
            <p className="drawer-version-note">
              Creates the consignment in Cargonizer and links the resulting shipment back to this
              request (sets logistics to Cargonizer).
            </p>
          </section>

          <PickListsSection request={data} disabled={disabled} />
        </div>
      </div>

      <p className="drawer-version-note">
        Version {data.version}
        {data.lastModifiedBy ? ` · last changed by ${data.lastModifiedBy}` : ''}
        {data.lastModified ? ` · ${new Date(data.lastModified).toLocaleString()}` : ''}
      </p>

      {handingOff ? (
        <HandOffDialog
          request={data}
          people={assigneeOptions}
          onClose={() => setHandingOff(false)}
          onReload={() => {
            setHandingOff(false);
            void request.refetch();
          }}
          onDone={(updated) => {
            setHandingOff(false);
            showUpdate(updated);
          }}
        />
      ) : null}
      {showCargonizer ? (
        <NewShipmentDrawer
          initialReason={data.title}
          initialSiteId={data.siteId}
          onClose={() => setShowCargonizer(false)}
          onCreated={(shipmentId) => {
            setShowCargonizer(false);
            mutation.mutate({
              logistics: {
                mode: 'CARGONIZER',
                shipmentId,
                ...(data.logistics?.notes ? { notes: data.logistics.notes } : {}),
              },
            });
          }}
        />
      ) : null}
    </>
  );
}
